import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, ArrowRightLeft } from 'lucide-react';
import { trackFantasyEvent } from '@/analytics';
import { ContentAdSlot } from '@/ads';
import { useFetchTeam } from '@/hooks/useFetchTeam';
import { useTeamCreateTournamentCalendarId } from '@/hooks/useTeamCreateTournamentCalendarId';
import { useTeamTransferHistory } from '@/hooks/useTeamTransferHistory';
import { useTeamRankHistory } from '@/hooks/useTeamRankHistory';
import { getErrorMessage, type BETeamTransferHistoryGameweek } from '@/lib/network';
import { useAuthStore } from '@/stores/AuthStore';
import { useUserTeamStore } from '@/stores/UserTeamStore';
import { TabGroup, TeamShirt, Typography, toCurrencyPriceMillions } from '@dreamteam-monorepo/ui';
import { Link } from 'react-router';

const RankHistoryChart = lazy(() =>
  import('./components/RankHistoryChart').then(module => ({ default: module.RankHistoryChart }))
);

type HistoryTab = 'transfer-history' | 'rank-history';

type HistoryPlayer = {
  name: string;
  position: 'GK' | 'DEF' | 'MID' | 'STR';
  team: string;
  shirtKey: string;
  price: string;
  points: string;
};

type HistoryTransferPair = {
  out: HistoryPlayer;
  in: HistoryPlayer;
};

type HistoryTransferWeek = {
  gameweek: number;
  totalPoints: number;
  transfers: HistoryTransferPair[];
};

const historyTabs = [
  { id: 'transfer-history', label: 'Transfer History' },
  { id: 'rank-history', label: 'Rank History' },
] as const;

const pointsClassName = (points: number) => {
  if (points > 0) {
    return 'bg-[--color-green-100]';
  }

  if (points < 0) {
    return 'bg-[--color-red-100]';
  }

  return 'bg-white';
};

const normalizePosition = (position: string | null): HistoryPlayer['position'] => {
  const normalizedPosition = position?.trim().toUpperCase();

  switch (normalizedPosition) {
    case 'GK':
    case 'DEF':
    case 'MID':
      return normalizedPosition;
    case 'FWD':
    case 'STR':
      return 'STR';
    default:
      return 'MID';
  }
};

const formatPlayerPoints = (points: number) => {
  const absolutePoints = Math.abs(points);
  return `${points}${absolutePoints === 1 ? 'pt' : 'pts'}`;
};

const mapHistoryPlayer = (
  player: BETeamTransferHistoryGameweek['transfers'][number]['transferIn']
): HistoryPlayer => {
  return {
    name: player.displayName,
    position: normalizePosition(player.position),
    team: player.contestantShortName ?? player.contestantName ?? player.shirtKey ?? 'Unknown',
    shirtKey: player.shirtKey ?? player.contestantFlagKey ?? '',
    price: toCurrencyPriceMillions(player.price),
    points: formatPlayerPoints(player.points),
  };
};

const mapTransferHistory = (
  items: BETeamTransferHistoryGameweek[] | undefined
): HistoryTransferWeek[] => {
  return (items ?? []).map(week => ({
    gameweek: week.gameweek,
    totalPoints: week.pointsDelta,
    transfers: week.transfers.map(transfer => ({
      out: mapHistoryPlayer(transfer.transferOut),
      in: mapHistoryPlayer(transfer.transferIn),
    })),
  }));
};

const PlayerCell = ({
  player,
  compact = false,
  align = 'left',
  direction,
}: {
  player: HistoryPlayer;
  compact?: boolean;
  align?: 'left' | 'right';
  direction?: 'out' | 'in';
}) => {
  return (
    <div
      className={[
        'flex items-center gap-[--padding-sm]',
        align === 'right' ? 'flex-row-reverse text-right' : '',
        compact ? 'py-[--padding-2xs] md:py-[--padding-xs]' : 'px-[--padding-sm] py-[--padding-xs]',
      ].join(' ')}
    >
      <div className="relative flex h-[52px] w-[60px] shrink-0 items-center justify-center overflow-visible">
        <TeamShirt
          team={player.shirtKey}
          position={player.position}
          assetScale="1x"
          className="!relative !z-0 !flex !h-[52px] !w-[60px] !items-center !justify-center [&>img]:!h-[50px] [&>img]:!w-auto [&>img]:!max-w-none"
        />
        {direction ? (
          <span
            className={[
              'absolute left-1/2 top-1/2 z-10 inline-flex size-[17px] min-h-[17px] min-w-[17px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-white shadow-[0_2px_6px_rgba(34,37,38,0.24)]',
              direction === 'out' ? 'bg-[#eb1701]' : 'bg-[#00a651]',
            ].join(' ')}
          >
            <ArrowRightLeft size={10} className={direction === 'in' ? 'scale-x-[-1]' : undefined} />
          </span>
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <Typography
          variant="subheadline020"
          className="truncate text-[--color-neutral-900] md:text-16 md:leading-20"
        >
          {player.name}
        </Typography>
        <Typography
          variant="paragraph010"
          className="truncate text-[--color-neutral-700] md:text-12 md:leading-16"
        >
          {player.position} | {player.team}
        </Typography>
        <Typography
          variant="paragraph010"
          className="truncate text-[--color-neutral-700] md:text-12 md:leading-16"
        >
          {player.price} | {player.points}
        </Typography>
      </div>
    </div>
  );
};

const TransferHistoryDesktop = ({ weeks }: { weeks: HistoryTransferWeek[] }) => {
  return (
    <div className="overflow-hidden rounded-[--corner-md] border border-[--color-grey-200] bg-white shadow-[0px_4px_24px_0px_rgba(34,37,38,0.08)]">
      <table className="w-full border-collapse table-fixed">
        <thead>
          <tr className="border-b border-[#eae9fd] bg-white">
            <th className="w-[88px] px-[--padding-xs] py-[--padding-sm] text-center">
              <Typography
                variant="paragraph000"
                className="text-center text-[#757777] md:text-12 md:leading-16"
              >
                Gameweek
              </Typography>
            </th>
            <th className="border-l border-[#eae9fd] px-[--padding-xs] py-[--padding-sm] text-left">
              <Typography
                variant="paragraph000"
                className="text-[#757777] md:text-12 md:leading-16"
              >
                Transferred Out
              </Typography>
            </th>
            <th className="w-[56px] px-[--padding-xs] py-[--padding-sm] text-center">
              <span className="sr-only">Transfer</span>
            </th>
            <th className="px-[--padding-xs] py-[--padding-sm] text-right">
              <Typography
                variant="paragraph000"
                className="text-[#757777] md:text-12 md:leading-16"
              >
                Transferred In
              </Typography>
            </th>
            <th className="w-[88px] border-l border-[#eae9fd] px-[--padding-xs] py-[--padding-sm] text-center">
              <Typography
                variant="paragraph000"
                className="text-center text-[#757777] md:text-12 md:leading-16"
              >
                Points
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {weeks.map(week => (
            <tr key={week.gameweek} className="border-b border-[--color-grey-200] last:border-b-0">
              <td className="align-middle px-[--padding-md] py-[--padding-md]">
                <div className="flex items-center justify-center">
                  <Typography
                    as="p"
                    variant="subheadline050"
                    className="text-[--color-neutral-900] md:text-24 md:leading-32"
                  >
                    {week.gameweek}
                  </Typography>
                </div>
              </td>
              <td className="align-top border-l border-[--color-grey-200] px-[--padding-md] py-[--padding-md]">
                <div className="grid gap-[--padding-sm]">
                  {week.transfers.map((transfer, index) => (
                    <div
                      key={`${week.gameweek}-${transfer.out.name}`}
                      className={index === 0 ? '' : 'pt-[--padding-sm]'}
                    >
                      <PlayerCell player={transfer.out} compact direction="out" />
                    </div>
                  ))}
                </div>
              </td>
              <td className="align-middle px-[--padding-sm] py-[--padding-md]">
                <div className="flex items-center justify-center">
                  <div className="flex size-[42px] min-h-[42px] min-w-[42px] shrink-0 items-center justify-center rounded-full bg-[--color-blue-200] text-white aspect-square md:size-[44px] md:min-h-[44px] md:min-w-[44px]">
                    <ArrowRightLeft className="h-4 w-4 md:h-[18px] md:w-[18px]" />
                  </div>
                </div>
              </td>
              <td className="align-top px-[--padding-md] py-[--padding-md]">
                <div className="grid gap-[--padding-sm]">
                  {week.transfers.map((transfer, index) => (
                    <div
                      key={`${week.gameweek}-${transfer.in.name}-in`}
                      className={index === 0 ? '' : 'pt-[--padding-sm]'}
                    >
                      <PlayerCell player={transfer.in} compact align="right" direction="in" />
                    </div>
                  ))}
                </div>
              </td>
              <td
                className={`align-middle border-l border-[--color-grey-200] px-[--padding-md] py-[--padding-md] ${pointsClassName(week.totalPoints)}`}
              >
                <div className="flex items-center justify-center">
                  <Typography
                    as="p"
                    variant="subheadline030"
                    className="!font-[900] text-[--color-neutral-900]"
                  >
                    {week.totalPoints > 0 ? '+' : ''}
                    {week.totalPoints}
                  </Typography>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TransferHistoryMobile = ({ weeks }: { weeks: HistoryTransferWeek[] }) => {
  return (
    <div className="grid gap-[--padding-md] md:hidden">
      {weeks.map(week => (
        <article
          key={week.gameweek}
          className="overflow-hidden rounded-[--corner-md] border border-[--color-grey-200] bg-white shadow-[0px_4px_24px_0px_rgba(34,37,38,0.08)]"
        >
          <div className="border-b border-[--color-grey-200] bg-[--page-header-bg-grey] px-[--padding-md] py-[--padding-sm]">
            <div className="flex items-center justify-between gap-[--padding-sm]">
              <Typography variant="label020" className="text-[--color-neutral-900]">
                Gameweek {week.gameweek}
              </Typography>
              <Typography
                variant="subheadline020"
                className={`${pointsClassName(week.totalPoints)} rounded-[--corner-xs] px-[--padding-xs] py-[--padding-2xs] !font-[900] text-[--color-neutral-900]`}
              >
                {week.totalPoints > 0 ? '+' : ''}
                {week.totalPoints}
              </Typography>
            </div>
          </div>

          <div className="divide-y divide-[--color-grey-200]">
            {week.transfers.map(transfer => (
              <div
                key={`${week.gameweek}-${transfer.out.name}-${transfer.in.name}`}
                className="grid gap-[--padding-sm] px-[--padding-md] py-[--padding-md]"
              >
                <div className="grid gap-[--padding-2xs]">
                  <Typography variant="paragraph010" className="text-[--color-neutral-700]">
                    TRANSFERRED OUT
                  </Typography>
                  <PlayerCell player={transfer.out} direction="out" />
                </div>

                <div className="grid gap-[--padding-2xs]">
                  <Typography variant="paragraph010" className="text-[--color-neutral-700]">
                    TRANSFERRED IN
                  </Typography>
                  <PlayerCell player={transfer.in} direction="in" />
                </div>
              </div>
            ))}
          </div>
        </article>
      ))}
    </div>
  );
};

const TransferHistoryState = ({ message }: { message: string }) => {
  return (
    <div className="rounded-[--corner-md] border border-[--color-grey-200] bg-white px-[--padding-lg] py-[--padding-xl] text-center shadow-[0px_4px_24px_0px_rgba(34,37,38,0.08)]">
      <Typography variant="subheadline020" className="text-[--color-neutral-900]">
        {message}
      </Typography>
    </div>
  );
};

export const MyTeamHistoryPage = () => {
  const [activeTab, setActiveTab] = useState<HistoryTab>('transfer-history');
  const sessionState = useAuthStore(s => s.sessionState);
  const userId = useAuthStore(s => s.userId);
  const backendTeamId = useUserTeamStore(s => s.backendTeamId);
  const setBackendTeamId = useUserTeamStore(s => s.setBackendTeamId);
  const { id: tournamentCalendarId, hasResolved: hasResolvedTournamentCalendar } =
    useTeamCreateTournamentCalendarId();
  const isAuthenticated = sessionState === 'authenticated';
  const existingTeamQuery = useFetchTeam(userId ?? undefined, tournamentCalendarId, {
    enabled: hasResolvedTournamentCalendar && isAuthenticated && !!userId && !backendTeamId,
  });

  const effectiveTeamId = backendTeamId ?? existingTeamQuery.data?.id;

  useEffect(() => {
    if (!backendTeamId && existingTeamQuery.data?.id) {
      setBackendTeamId(existingTeamQuery.data.id);
    }
  }, [backendTeamId, existingTeamQuery.data?.id, setBackendTeamId]);

  const transferHistoryQuery = useTeamTransferHistory(effectiveTeamId, {
    enabled: isAuthenticated && !!userId && !!effectiveTeamId,
  });
  const rankHistoryQuery = useTeamRankHistory(effectiveTeamId, {
    enabled: activeTab === 'rank-history' && isAuthenticated && !!userId && !!effectiveTeamId,
  });

  const transferHistory = useMemo(
    () => mapTransferHistory(transferHistoryQuery.data),
    [transferHistoryQuery.data]
  );

  const handleChangeHistoryTab = (id: string) => {
    const nextTab = id as HistoryTab;
    setActiveTab(nextTab);

    if (nextTab === activeTab) return;
    trackFantasyEvent(
      nextTab === 'transfer-history' ? 'transferHistoryOpened' : 'rankHistoryOpened'
    );
  };

  const transferHistoryState = (() => {
    if (
      !hasResolvedTournamentCalendar ||
      sessionState === 'resolving' ||
      existingTeamQuery.isPending
    ) {
      return 'loading';
    }

    if (!isAuthenticated || !userId) {
      return 'empty';
    }

    if (!effectiveTeamId) {
      return 'empty';
    }

    if (transferHistoryQuery.isPending) {
      return 'loading';
    }

    if (transferHistoryQuery.isError) {
      return 'error';
    }

    if (transferHistory.length === 0) {
      return 'empty';
    }

    return 'ready';
  })();

  return (
    <div className="components light flex flex-col bg-white">
      <section className="bg-[--page-header-bg-grey]">
        <div className="mx-auto grid w-full max-w-[846px] grid-cols-[minmax(0,1fr)_auto] items-center gap-[--padding-md] px-[--padding-md] py-[--padding-xl] lg:grid-cols-[minmax(0,1fr)_minmax(320px,360px)_auto]">
          <Typography
            as="h1"
            variant="headline050"
            className="text-[--page-header-header] !font-[900] sm:text-40 sm:leading-44"
          >
            {activeTab === 'rank-history' ? 'RANK HISTORY' : 'TRANSFER HISTORY'}
          </Typography>

          <div className="order-3 col-span-2 w-full lg:order-2 lg:col-span-1">
            <TabGroup
              tabs={[...historyTabs]}
              selectedId={activeTab}
              onChangeTab={handleChangeHistoryTab}
            />
          </div>

          <Link to="/my-team" className="order-2 flex items-center gap-2 lg:order-3">
            <ArrowLeftIcon />
            Back
          </Link>
        </div>
      </section>

      <main className="mx-auto flex w-full max-w-[846px] flex-col gap-[--padding-xl] px-4 py-[--padding-xl] sm:px-0">
        {activeTab === 'transfer-history' ? (
          transferHistoryState === 'ready' ? (
            <>
              <TransferHistoryMobile weeks={transferHistory} />
              <div className="hidden md:block">
                <TransferHistoryDesktop weeks={transferHistory} />
              </div>
            </>
          ) : transferHistoryState === 'error' ? (
            <TransferHistoryState
              message={getErrorMessage(
                transferHistoryQuery.error,
                'Failed to load transfer history.'
              )}
            />
          ) : transferHistoryState === 'empty' ? (
            <TransferHistoryState message="No completed transfer history available yet." />
          ) : (
            <TransferHistoryState message="Loading transfer history..." />
          )
        ) : !hasResolvedTournamentCalendar ||
          sessionState === 'resolving' ||
          existingTeamQuery.isPending ||
          rankHistoryQuery.isPending ? (
          <TransferHistoryState message="Loading rank history..." />
        ) : rankHistoryQuery.isError ? (
          <TransferHistoryState
            message={getErrorMessage(rankHistoryQuery.error, 'Failed to load rank history.')}
          />
        ) : !rankHistoryQuery.data?.length ? (
          <TransferHistoryState message="No completed rank history available yet." />
        ) : (
          <Suspense fallback={<TransferHistoryState message="Loading rank history chart..." />}>
            <RankHistoryChart items={rankHistoryQuery.data} />
          </Suspense>
        )}
      </main>

      <ContentAdSlot id="ad-boa-leaderboard" />
    </div>
  );
};
